import { useEffect, useRef, useState, type PropsWithChildren } from 'react';

import { useAuth } from '../context/AuthContext';
import {
  clearDemographicsCache,
  hydrateDemographics,
  needsDemographicsPrompt,
} from '../services/demographicsService';
import type { AccountUserProfileRecord } from '../services/accountUserProfileService';
import { DemographicsPromptModal } from './DemographicsPromptModal';

const PROMPT_DELAY_MS = 1400;

export function DemographicsPromptHost({ children }: PropsWithChildren) {
  const { status, user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [profile, setProfile] = useState<AccountUserProfileRecord | null>(null);
  const dismissedThisSessionRef = useRef(false);
  const checkedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (status !== 'signed-in' || !user?.id) {
      setVisible(false);
      return;
    }

    if (dismissedThisSessionRef.current || checkedUserIdRef.current === user.id) {
      return;
    }

    const timerId = setTimeout(() => {
      void (async () => {
        const nextProfile = await hydrateDemographics(user.id);
        checkedUserIdRef.current = user.id;
        setProfile(nextProfile);

        if (dismissedThisSessionRef.current) {
          return;
        }

        if (needsDemographicsPrompt(nextProfile)) {
          setVisible(true);
        }
      })();
    }, PROMPT_DELAY_MS);

    return () => {
      clearTimeout(timerId);
    };
  }, [status, user?.id]);

  useEffect(() => {
    if (status === 'signed-out') {
      dismissedThisSessionRef.current = false;
      checkedUserIdRef.current = null;
      clearDemographicsCache();
      setProfile(null);
      setVisible(false);
    }
  }, [status]);

  const handleDismiss = () => {
    dismissedThisSessionRef.current = true;
    setVisible(false);
  };

  const handleSaved = () => {
    dismissedThisSessionRef.current = true;
    setVisible(false);
  };

  return (
    <>
      {children}
      {user?.id ? (
        <DemographicsPromptModal
          visible={visible}
          userId={user.id}
          initialProfile={profile}
          onDismiss={handleDismiss}
          onSaved={handleSaved}
        />
      ) : null}
    </>
  );
}
